/**
 * FounsiScreen — the AI chat tab (FE #259 / Journey 9).
 *
 * Streaming SSE chat with the Founsi agent backed by
 * `POST /assistant/chat`.  Web-only streaming in v1 (see
 * streamAssistantChat).  Native ships a placeholder card.
 *
 * UX shape:
 *   ┌────────────────────────────────────┐
 *   │ Header (thread title / "+" new)     │
 *   │ ───────────────────────────────── │
 *   │ Bubbles (User & Assistant)          │
 *   │   - Tool events render as a thin    │
 *   │     "ran lookup_*" chip inline.     │
 *   │   - Errors render with a retry CTA. │
 *   │ ───────────────────────────────── │
 *   │ Empty state: suggestion chips       │
 *   │ ───────────────────────────────── │
 *   │ Composer: textinput + send btn      │
 *   └────────────────────────────────────┘
 *
 * The screen DOES NOT mount any server reads on focus (no thread-list
 * fetch).  That's a follow-up issue (#261).  For v1, the user lands on
 * a fresh thread; the BE auto-creates one on first turn.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Image,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { goBackOrHome } from '../../navigation/goBackOrHome';
import { fpColors } from '../../styles/theme';
import {
  Bubble,
  AssistantBubble,
  UserBubble,
  appendAssistantPlaceholder,
  appendUserBubble,
  applySseEvent,
  abortStream,
  setPreStreamError,
  startNewThread,
} from './founsiSlice';
import {
  AssistantHttpError,
  streamAssistantChat,
} from './api/streamAssistantChat';
import { api } from '../../api/remembrApi';
import {
  parseItemsFromToolResult,
  type FounsiItem,
} from './api/parseItemsFromToolResult';
import {
  parseArtifactsFromToolEvent,
  type FounsiArtifact,
} from './api/parseArtifactsFromToolEvent';
import { FounsiItemRow } from './components/FounsiItemRow';
import { CustodyChip } from './components/artifacts/CustodyChip';
import { LocationGroupCard } from './components/artifacts/LocationGroupCard';
import { HistorySheet } from './components/HistorySheet';
import { prependThread } from './founsiHistorySlice';

// ─── Suggestion chips for empty state ────────────────────────────────
//
// Grouped from `docs/founsi/dataset-v1.jsonl` — these are the
// canonical "what can I ask?" prompts that exercise the agent's
// supported tool surface.

interface Suggestion {
  label: string;
  prompt: string;
}

const SUGGESTION_GROUPS: Array<{ heading: string; items: Suggestion[] }> = [
  {
    heading: 'Find',
    items: [
      { label: 'Where is my Anker SOLIX?', prompt: 'Where is my Anker SOLIX?' },
      { label: 'Show all my cameras', prompt: 'Show me all my cameras.' },
      {
        label: 'Tools in the garage',
        prompt: 'What tools do I have in the garage?',
      },
    ],
  },
  {
    heading: 'Recall',
    items: [
      {
        label: 'Who has my drill?',
        prompt: 'Who currently has my drill?',
      },
      {
        label: 'What did I lend to Sarah?',
        prompt: 'What did I lend to Sarah?',
      },
    ],
  },
  {
    heading: 'Plan',
    items: [
      {
        label: 'Pack for a camping trip',
        prompt: 'Help me pack for a weekend camping trip.',
      },
      {
        label: 'What do I have for video work?',
        prompt: 'What gear do I have for video production?',
      },
    ],
  },
];

// ─── Bubble subcomponents ──────────────────────────────────────────

const UserBubbleView: React.FC<{ bubble: UserBubble }> = ({ bubble }) => {
  const photo = bubble.attachments?.find((a) => a.type === 'image' && a.preview);
  return (
    <View style={styles.userRow} testID={`founsi-bubble-user-${bubble.localId}`}>
      <View style={styles.userBubble}>
        {photo?.preview ? (
          <Image source={{ uri: photo.preview }} style={styles.userBubblePhoto} />
        ) : null}
        {bubble.text ? (
          <Text style={styles.userBubbleText}>{bubble.text}</Text>
        ) : null}
      </View>
    </View>
  );
};

const ToolChip: React.FC<{
  tool: string;
  status?: string;
  errorCode?: string | null;
}> = ({ tool, status, errorCode }) => {
  const failed = !!errorCode || status === 'error';
  const pending = !status;
  return (
    <View
      style={[
        styles.toolChip,
        failed && styles.toolChipFailed,
        pending && styles.toolChipPending,
      ]}
    >
      <MaterialIcons
        name={failed ? 'error-outline' : pending ? 'hourglass-empty' : 'check'}
        size={11}
        color={failed ? fpColors.error ?? '#B00020' : fpColors.onSurfaceVariant}
      />
      <Text style={styles.toolChipText}>{tool}</Text>
    </View>
  );
};

const AssistantBubbleView: React.FC<{
  bubble: AssistantBubble;
  onRetry: () => void;
}> = ({ bubble, onRetry }) => {
  const showSpinner = !bubble.complete && !bubble.text;

  // Flatten + dedupe inventory cards across every tool event on this
  // bubble.  Deduping at this layer means a bubble that calls two
  // overlapping search tools doesn't show the same item twice.
  const items: FounsiItem[] = (() => {
    const seen = new Map<number, FounsiItem>();
    for (const te of bubble.toolEvents) {
      if (!te.result || te.result.status !== 'ok') continue;
      for (const it of parseItemsFromToolResult(te.tool, te.result.data)) {
        if (!seen.has(it.inventory_item_id)) seen.set(it.inventory_item_id, it);
      }
    }
    return Array.from(seen.values());
  })();

  // Other typed artifacts (custody / location / etc).  Emitted in
  // the order their tools fired.  Deduped by a synthetic key so a
  // bubble that asks `who_has_item` twice for the same id doesn't
  // double-render the chip.
  const artifacts: FounsiArtifact[] = (() => {
    const seen = new Set<string>();
    const out: FounsiArtifact[] = [];
    for (const te of bubble.toolEvents) {
      if (!te.result) continue;
      for (const a of parseArtifactsFromToolEvent(
        te.tool,
        te.result.status,
        te.result.data,
      )) {
        const key =
          a.type === 'custody'
            ? `custody:${a.item_id}`
            : `loc:${a.location}:${a.items.map((i) => i.item_id).join(',')}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(a);
      }
    }
    return out;
  })();

  return (
    <View
      style={styles.assistantRow}
      testID={`founsi-bubble-assistant-${bubble.localId}`}
    >
      <View style={styles.assistantAvatar}>
        <MaterialIcons name="auto-awesome" size={14} color={fpColors.primary} />
      </View>
      <View style={styles.assistantBubble}>
        {/* Inline tool-event log — sits above the answer text */}
        {bubble.toolEvents.length > 0 ? (
          <View style={styles.toolChipsRow}>
            {bubble.toolEvents.map((te) => (
              <ToolChip
                key={te.call_id}
                tool={te.tool}
                status={te.result?.status}
                errorCode={te.result?.error_code}
              />
            ))}
          </View>
        ) : null}

        {/* Typed artifacts (custody chip, location group) — render
            in tool-emission order, above the inventory carousel. */}
        {artifacts.map((a, i) =>
          a.type === 'custody' ? (
            <CustodyChip key={`art-${i}`} artifact={a} />
          ) : a.type === 'location_group' ? (
            <LocationGroupCard key={`art-${i}`} artifact={a} />
          ) : null,
        )}

        {/* Structured item cards from navigable tool results */}
        {items.length > 0 ? <FounsiItemRow items={items} /> : null}

        {showSpinner ? (
          <View style={styles.thinkingRow}>
            <ActivityIndicator size="small" color={fpColors.primary} />
            <Text style={styles.thinkingText}>Thinking…</Text>
          </View>
        ) : (
          <Text style={styles.assistantBubbleText}>{bubble.text}</Text>
        )}

        {bubble.errored ? (
          <View style={styles.errorBlock}>
            <Text style={styles.errorText}>
              {bubble.errorMessage ?? "Something went wrong."}
            </Text>
            {bubble.errorRetryable ? (
              <Pressable
                style={styles.retryBtn}
                onPress={onRetry}
                testID="founsi-retry"
              >
                <MaterialIcons name="refresh" size={14} color={fpColors.primary} />
                <Text style={styles.retryBtnText}>Try again</Text>
              </Pressable>
            ) : null}
          </View>
        ) : null}
      </View>
    </View>
  );
};

// ─── Empty-state suggestions ───────────────────────────────────────

const EmptyState: React.FC<{ onPick: (prompt: string) => void }> = ({
  onPick,
}) => (
  <View style={styles.emptyWrap}>
    <View style={styles.emptyHeroIconWrap}>
      <MaterialIcons
        name="auto-awesome"
        size={28}
        color={fpColors.primary}
      />
    </View>
    <Text style={styles.emptyTitle}>Ask Founsi</Text>
    <Text style={styles.emptySubtitle}>
      Find an item, recall a loan, or plan what to pack — all in one
      sentence.
    </Text>
    {SUGGESTION_GROUPS.map((g) => (
      <View key={g.heading} style={styles.suggestionGroup}>
        <Text style={styles.suggestionGroupHeading}>{g.heading}</Text>
        <View style={styles.suggestionRow}>
          {g.items.map((s) => (
            <Pressable
              key={s.label}
              style={({ pressed }) => [
                styles.suggestionChip,
                pressed && styles.suggestionChipPressed,
              ]}
              onPress={() => onPick(s.prompt)}
              testID={`founsi-suggestion-${s.label}`}
            >
              <Text style={styles.suggestionChipText}>{s.label}</Text>
            </Pressable>
          ))}
        </View>
      </View>
    ))}
  </View>
);

// ─── Screen ────────────────────────────────────────────────────────

export const FounsiScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();
  const { bubbles, status, threadId, threadTitle } = useAppSelector(
    (s) => s.founsi,
  );

  const [draft, setDraft] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  /** Single pending photo attachment for the next outgoing message.
   *  v1 is one-photo-at-a-time — the BE accepts an array, but the
   *  composer UI is simpler with a single attachment. */
  const [pendingAttachment, setPendingAttachment] = useState<{
    file_id: string;
    /** Browser-side object URL for the immediate preview thumbnail. */
    preview_url: string;
  } | null>(null);
  /** 0..100 while uploading, null when idle. */
  const [attachUploadProgress, setAttachUploadProgress] = useState<number | null>(
    null,
  );
  const abortRef = useRef<AbortController | null>(null);
  const listRef = useRef<FlatList<Bubble>>(null);
  /** Hidden web file input (web only).  We trigger the picker via
   *  `.click()` from the paperclip button. */
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Auto-scroll to the latest bubble when content streams in.
  useEffect(() => {
    if (bubbles.length === 0) return;
    // Defer to next frame so RN measures the new row.
    const id = setTimeout(() => {
      listRef.current?.scrollToEnd({ animated: true });
    }, 16);
    return () => clearTimeout(id);
  }, [bubbles.length, bubbles[bubbles.length - 1]]);

  const isSending = status === 'submitting' || status === 'thinking' || status === 'streaming';

  // ── Photo-attach: open the OS picker on the hidden web input.
  //    On iOS Safari this offers Camera + Library; on desktop it's
  //    the file chooser.  Native build will need its own path
  //    (WebMobileCameraView is web-only today).
  const onPickPhoto = useCallback(() => {
    if (Platform.OS !== 'web' || !fileInputRef.current) return;
    fileInputRef.current.click();
  }, []);

  const onFilePicked = useCallback(
    async (e: any) => {
      const file: File | undefined = e?.target?.files?.[0];
      // Reset the input so picking the SAME file twice fires onChange.
      if (e?.target) e.target.value = '';
      if (!file) return;
      // Show the preview immediately, before the upload resolves.
      const preview_url = URL.createObjectURL(file);
      setPendingAttachment({ file_id: 'pending', preview_url });
      setAttachUploadProgress(0);
      try {
        const uploaded = await api.uploadFile(
          { uri: preview_url, name: file.name, type: file.type },
          undefined,
          (pct) => setAttachUploadProgress(pct),
        );
        const id = (uploaded as any).id ?? (uploaded as any).file_record_id;
        if (id == null) throw new Error('Upload returned no file id');
        setPendingAttachment({ file_id: String(id), preview_url });
        setAttachUploadProgress(null);
      } catch (err) {
        setPendingAttachment(null);
        setAttachUploadProgress(null);
        if (typeof window !== 'undefined' && window.alert) {
          window.alert(
            "Couldn't attach that photo: " +
              ((err as Error)?.message ?? 'unknown error'),
          );
        }
      }
    },
    [],
  );

  const onClearAttachment = useCallback(() => {
    if (pendingAttachment?.preview_url) {
      try {
        URL.revokeObjectURL(pendingAttachment.preview_url);
      } catch {
        /* noop */
      }
    }
    setPendingAttachment(null);
    setAttachUploadProgress(null);
  }, [pendingAttachment]);

  // ── Pure submit pipeline.  Caller is responsible for clearing the
  //    composer / managing focus.
  const submit = useCallback(
    async (message: string) => {
      const trimmed = message.trim();
      // Allow sending if EITHER a text message OR a pending attachment
      // is set — photo-only "what is this?" is a valid query shape.
      if (!trimmed && !pendingAttachment) return;
      // Block sending while an attachment is still uploading.
      if (attachUploadProgress !== null) return;
      const localUserId = `u-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
      const localAssistantId = `a-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
      // Snapshot the attachment for THIS turn before clearing the
      // composer state — pending state is single-shot.
      const turnAttachment = pendingAttachment;
      dispatch(
        appendUserBubble({
          kind: 'user',
          localId: localUserId,
          text: trimmed,
          attachments: turnAttachment
            ? [
                {
                  type: 'image',
                  file_id: turnAttachment.file_id,
                  preview: turnAttachment.preview_url,
                },
              ]
            : undefined,
          timestamp: new Date().toISOString(),
        }),
      );
      dispatch(appendAssistantPlaceholder({ localId: localAssistantId }));
      // Clear the composer attachment — the bubble owns the preview now.
      setPendingAttachment(null);

      const controller = new AbortController();
      abortRef.current = controller;
      try {
        for await (const ev of streamAssistantChat(
          {
            user_message: trimmed,
            thread_id: threadId,
            attachments: turnAttachment
              ? [{ type: 'image', file_id: turnAttachment.file_id }]
              : undefined,
          },
          { signal: controller.signal },
        )) {
          dispatch(applySseEvent(ev));
          // First-turn thread → optimistically prepend to the history
          // drawer so the freshly-created thread is visible without a
          // round-trip the next time the user opens the drawer.  The
          // `thread` SSE event only fires on the first turn of a new
          // thread, so this is naturally guarded.
          if (ev.event === 'thread') {
            const nowIso = new Date().toISOString();
            dispatch(
              prependThread({
                id: ev.thread_id,
                title: ev.title,
                pinned: false,
                preview: trimmed.slice(0, 60),
                last_message_at: nowIso,
                created_at: nowIso,
              }),
            );
          }
          if (ev.event === 'done' || ev.event === 'error') break;
        }
      } catch (err: unknown) {
        if (err instanceof AssistantHttpError) {
          dispatch(
            setPreStreamError({
              code: err.code,
              message: err.message,
              status: err.status,
            }),
          );
        } else if ((err as any)?.name === 'AbortError') {
          // user-initiated cancel — slice already in idle.
        } else {
          dispatch(
            setPreStreamError({
              code: null,
              message:
                (err as Error)?.message ??
                "Couldn't reach Founsi. Check your connection and try again.",
              status: 0,
            }),
          );
        }
      } finally {
        abortRef.current = null;
      }
    },
    [dispatch, threadId],
  );

  const onSubmit = useCallback(() => {
    const text = draft;
    setDraft('');
    submit(text);
  }, [draft, submit]);

  const onPickSuggestion = useCallback(
    (prompt: string) => {
      submit(prompt);
    },
    [submit],
  );

  const onRetryLast = useCallback(() => {
    // Repeat the most recent user bubble.
    const lastUser = [...bubbles].reverse().find((b) => b.kind === 'user');
    if (!lastUser) return;
    submit((lastUser as UserBubble).text);
  }, [bubbles, submit]);

  const onStop = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    dispatch(abortStream());
  }, [dispatch]);

  const onNewThread = useCallback(() => {
    onStop();
    dispatch(startNewThread());
  }, [dispatch, onStop]);

  const renderItem = useCallback(
    ({ item }: { item: Bubble }) =>
      item.kind === 'user' ? (
        <UserBubbleView bubble={item} />
      ) : (
        <AssistantBubbleView bubble={item} onRetry={onRetryLast} />
      ),
    [onRetryLast],
  );

  const showEmpty = bubbles.length === 0;
  // Send is enabled when EITHER we have text OR a ready attachment
  // (and we're not still uploading or mid-stream).
  const hasAttachmentReady =
    !!pendingAttachment && attachUploadProgress === null;
  const sendDisabled =
    isSending ||
    attachUploadProgress !== null ||
    (draft.trim().length === 0 && !hasAttachmentReady);

  return (
    <KeyboardAvoidingView
      style={styles.root}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 72 : 0}
    >
      {/* Header */}
      <View style={styles.header}>
        <Pressable
          onPress={() => setHistoryOpen(true)}
          style={styles.headerIconBtn}
          accessibilityRole="button"
          accessibilityLabel="History"
          testID="founsi-history-open"
        >
          <MaterialIcons
            name="history"
            size={20}
            color={fpColors.onSurface}
          />
        </Pressable>
        <Text style={styles.headerTitle} numberOfLines={1}>
          {threadTitle ?? 'Founsi'}
        </Text>
        <Pressable
          onPress={onNewThread}
          style={styles.headerIconBtn}
          accessibilityRole="button"
          accessibilityLabel="New thread"
          testID="founsi-new-thread"
          disabled={bubbles.length === 0}
        >
          <MaterialIcons
            name="add"
            size={20}
            color={
              bubbles.length === 0 ? fpColors.outline : fpColors.onSurface
            }
          />
        </Pressable>
      </View>

      {/* Body */}
      {showEmpty ? (
        <EmptyState onPick={onPickSuggestion} />
      ) : (
        <FlatList
          ref={listRef}
          data={bubbles}
          keyExtractor={(b) =>
            b.kind === 'user' ? `u-${b.localId}` : `a-${b.localId}`
          }
          renderItem={renderItem}
          contentContainerStyle={styles.listContent}
          keyboardShouldPersistTaps="handled"
          testID="founsi-bubble-list"
        />
      )}

      {/* Composer (with optional attachment preview row) */}
      {pendingAttachment ? (
        <View style={styles.attachmentRow}>
          <Image
            source={{ uri: pendingAttachment.preview_url }}
            style={styles.attachmentThumb}
          />
          <View style={styles.attachmentMeta}>
            <Text style={styles.attachmentTitle} numberOfLines={1}>
              {attachUploadProgress !== null
                ? `Uploading… ${attachUploadProgress}%`
                : 'Ready to send'}
            </Text>
            {attachUploadProgress !== null ? (
              <View style={styles.attachmentProgressTrack}>
                <View
                  style={[
                    styles.attachmentProgressFill,
                    { width: `${attachUploadProgress}%` },
                  ]}
                />
              </View>
            ) : null}
          </View>
          <Pressable
            onPress={onClearAttachment}
            style={styles.attachmentClearBtn}
            accessibilityLabel="Remove attachment"
            testID="founsi-attachment-clear"
          >
            <MaterialIcons name="close" size={16} color={fpColors.onSurface} />
          </Pressable>
        </View>
      ) : null}

      {/* Hidden file input on web — triggered by the paperclip btn. */}
      {Platform.OS === 'web' ? (
        <input
          ref={fileInputRef as any}
          type="file"
          accept="image/*"
          // capture="environment" hints camera on mobile Safari/Chrome,
          // while still allowing library on desktop.
          {...({ capture: 'environment' } as any)}
          style={{ display: 'none' } as any}
          onChange={onFilePicked as any}
          data-testid="founsi-file-input"
        />
      ) : null}

      <View style={styles.composer}>
        <Pressable
          onPress={onPickPhoto}
          style={styles.composerAttachBtn}
          accessibilityRole="button"
          accessibilityLabel="Attach photo"
          testID="founsi-attach"
          disabled={attachUploadProgress !== null}
        >
          <MaterialIcons
            name="add-photo-alternate"
            size={22}
            color={
              attachUploadProgress !== null
                ? fpColors.outline
                : fpColors.onSurfaceVariant
            }
          />
        </Pressable>
        <TextInput
          value={draft}
          onChangeText={setDraft}
          placeholder="Ask Founsi…"
          placeholderTextColor={fpColors.outline}
          style={styles.input}
          multiline
          editable={!isSending}
          onSubmitEditing={(e) => {
            if (!e.nativeEvent.text || isSending) return;
            onSubmit();
          }}
          blurOnSubmit={false}
          testID="founsi-composer-input"
        />
        {isSending ? (
          <Pressable
            onPress={onStop}
            style={styles.composerBtnStop}
            accessibilityRole="button"
            accessibilityLabel="Stop"
            testID="founsi-stop"
          >
            <MaterialIcons name="stop" size={18} color="#fff" />
          </Pressable>
        ) : (
          <Pressable
            onPress={onSubmit}
            disabled={sendDisabled}
            style={[
              styles.composerBtnSend,
              sendDisabled && styles.composerBtnSendDisabled,
            ]}
            accessibilityRole="button"
            accessibilityLabel="Send"
            testID="founsi-send"
          >
            <MaterialIcons name="arrow-upward" size={18} color="#fff" />
          </Pressable>
        )}
      </View>

      {/* Thread history drawer (#261 PR1) */}
      <HistorySheet
        visible={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />
    </KeyboardAvoidingView>
  );
};

// ─── Styles ────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: fpColors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surfaceContainerLowest,
    gap: 8,
  },
  headerIconBtn: {
    // Mobile: bumped from 36×36 → 44×44 to hit iOS HIG touch
    // minimum.  Icon size unchanged at 20px so the visual weight
    // doesn't change.
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    flex: 1,
    fontSize: 15,
    fontWeight: '600',
    color: fpColors.onSurface,
    textAlign: 'center',
  },
  listContent: {
    padding: 14,
    gap: 10,
  },
  userRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  userBubble: {
    maxWidth: '82%',
    backgroundColor: fpColors.primary,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 16,
    borderBottomRightRadius: 4,
  },
  userBubbleText: {
    color: '#fff',
    fontSize: 14,
    lineHeight: 20,
  },
  userBubblePhoto: {
    width: 180,
    height: 180,
    maxWidth: '100%',
    borderRadius: 10,
    marginBottom: 6,
    // Show the whole photo the user attached — no crop. Letterbox
    // bars tinted with a darker bg so they read as padding.
    resizeMode: 'contain',
    backgroundColor: fpColors.surfaceContainerLow,
  },
  attachmentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surfaceContainerLowest,
  },
  attachmentThumb: {
    width: 44,
    height: 44,
    borderRadius: 8,
    // Whole photo visible in the pending-attachment chip — letterbox
    // already-tinted via the surfaceContainerLow bg below.
    resizeMode: 'contain',
    backgroundColor: fpColors.surfaceContainerLow,
  },
  attachmentMeta: {
    flex: 1,
  },
  attachmentTitle: {
    fontSize: 12,
    color: fpColors.onSurface,
    fontWeight: '600',
  },
  attachmentProgressTrack: {
    marginTop: 4,
    height: 3,
    borderRadius: 2,
    backgroundColor: fpColors.surfaceContainerLow,
    overflow: 'hidden',
  },
  attachmentProgressFill: {
    height: '100%',
    backgroundColor: fpColors.primary,
  },
  attachmentClearBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  composerAttachBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  assistantRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  assistantAvatar: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: fpColors.surfaceContainerLow,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },
  assistantBubble: {
    flex: 1,
    backgroundColor: fpColors.surface,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 16,
    borderBottomLeftRadius: 4,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  assistantBubbleText: {
    fontSize: 14,
    lineHeight: 20,
    color: fpColors.onSurface,
  },
  thinkingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 2,
  },
  thinkingText: {
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
  },
  toolChipsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 6,
  },
  toolChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
    backgroundColor: fpColors.surfaceContainerLow,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  toolChipPending: {
    opacity: 0.65,
  },
  toolChipFailed: {
    borderColor: fpColors.error ?? '#B00020',
  },
  toolChipText: {
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
    fontFamily: Platform.select({
      web: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      default: undefined,
    }),
  },
  errorBlock: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: fpColors.outlineVariant,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  errorText: {
    flex: 1,
    fontSize: 12,
    color: fpColors.error ?? '#B00020',
  },
  retryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: fpColors.surfaceContainerLow,
  },
  retryBtnText: {
    fontSize: 12,
    color: fpColors.primary,
    fontWeight: '600',
  },
  emptyWrap: {
    flex: 1,
    paddingHorizontal: 18,
    paddingTop: 32,
    alignItems: 'stretch',
  },
  emptyHeroIconWrap: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: fpColors.surfaceContainerLow,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
  },
  emptyTitle: {
    marginTop: 12,
    fontSize: 22,
    fontWeight: '700',
    color: fpColors.onSurface,
    textAlign: 'center',
  },
  emptySubtitle: {
    marginTop: 6,
    fontSize: 14,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
    marginBottom: 22,
  },
  suggestionGroup: {
    marginBottom: 16,
  },
  suggestionGroupHeading: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.6,
    color: fpColors.onSurfaceVariant,
    marginBottom: 8,
    textTransform: 'uppercase',
  },
  suggestionRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  suggestionChip: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 999,
    backgroundColor: fpColors.surface,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  suggestionChipPressed: {
    backgroundColor: fpColors.surfaceContainerLow,
  },
  suggestionChipText: {
    fontSize: 13,
    color: fpColors.onSurface,
  },
  composer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surfaceContainerLowest,
  },
  input: {
    flex: 1,
    minHeight: 40,
    maxHeight: 120,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 20,
    backgroundColor: fpColors.surface,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    color: fpColors.onSurface,
    fontSize: 14,
  },
  composerBtnSend: {
    // Mobile: send/stop bumped from 40 → 44 to match iOS HIG.
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: fpColors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  composerBtnSendDisabled: {
    backgroundColor: fpColors.outline,
  },
  composerBtnStop: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: fpColors.error ?? '#B00020',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default FounsiScreen;
